import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import OwnerPageLayout from './layout/OwnerPageLayout';
import { getSchoolForSession, getSchoolIdFromResponse } from '../utils/schoolHelper';
import { getActiveSchoolId } from '../utils/activeSchool';
import { getApiBaseUrl } from '../apiBase';
import { isCoachAccount } from '../utils/roles';
import { useTranslation } from 'react-i18next';
import { formatDate, formatWeekdayDate } from '../utils/dateFormat';

const API = getApiBaseUrl();

export default function AttendanceCalendar() {
    const { t } = useTranslation(['attendanceCalendar', 'common', 'errors']);
    const months = useMemo(() => Array.from({ length: 12 }, (_, i) => t(`attendanceCalendar:month${i + 1}`)), [t]);
    const monthHeaders = useMemo(() => [
        t('attendanceCalendar:colStudent'),
        t('attendanceCalendar:colTotal'),
        t('attendanceCalendar:colPresent'),
        t('attendanceCalendar:colAbsent'),
        t('attendanceCalendar:colPercent'),
    ], [t]);
    const coachMode = isCoachAccount();
    const [searchParams] = useSearchParams();
    const [schoolId, setSchoolId] = useState(null);
    const [groups, setGroups] = useState([]);
    const [groupId, setGroupId] = useState('');
    const [sessions, setSessions] = useState([]);
    const [selectedSlotId, setSelectedSlotId] = useState('');
    const [date, setDate] = useState(searchParams.get('date') || todayStr());
    const [students, setStudents] = useState([]);
    const [marks, setMarks] = useState({});
    const [calendar, setCalendar] = useState([]);
    const [view, setView] = useState(coachMode ? 'day' : 'day');
    const [saving, setSaving] = useState(false);
    const [sessionInfo, setSessionInfo] = useState({ hasSession: true, defaultAssumeAttending: true });

    const token = localStorage.getItem('token');
    const api = axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });

    function todayStr() {
        return new Date().toISOString().split('T')[0];
    }

    useEffect(() => {
        initSchool();
    }, []);

    useEffect(() => {
        const g = searchParams.get('groupId');
        if (g) setGroupId(g);
    }, [searchParams]);

    useEffect(() => {
        if (!schoolId) return;
        if (coachMode) {
            loadSessions();
            return;
        }
        setGroupId('');
        setStudents([]);
        setMarks({});
        loadGroups();
    }, [schoolId, coachMode]);

    useEffect(() => {
        if (!groupId || !schoolId) return;
        if (view === 'day') loadDayAttendance();
        if (view === 'month' && !coachMode) loadMonthCalendar();
    }, [groupId, date, view, schoolId]);

    async function initSchool() {
        try {
            const activeId = getActiveSchoolId();
            const schoolRes = await getSchoolForSession(api);
            const sid = getSchoolIdFromResponse(schoolRes) || activeId;
            if (!sid) {
                alert(t('attendanceCalendar:noSchool'));
                return;
            }
            setSchoolId(sid);
        } catch {
            alert(t('attendanceCalendar:loadSchoolError'));
        }
    }

    async function loadGroups() {
        if (!schoolId) return;
        try {
            const res = await api.get(`/school/${schoolId}/groups`);
            const list = res.data || [];
            setGroups(list);
            const urlGid = searchParams.get('groupId');
            if (urlGid && list.some(g => String(g.id) === urlGid)) {
                setGroupId(urlGid);
            } else if (list.length > 0) {
                setGroupId(String(list[0].id));
            } else {
                setGroupId('');
            }
        } catch { alert(t('attendanceCalendar:loadGroupsError')); }
    }

    async function loadSessions() {
        if (!schoolId) return;
        setStudents([]);
        setMarks({});
        setSelectedSlotId('');
        setGroupId('');
        try {
            const res = await api.get('/attendance/sessions', { params: { schoolId, date } });
            const list = res.data || [];
            setSessions(list);
            const urlGid = searchParams.get('groupId');
            if (urlGid) {
                const match = list.find(s => String(s.groupId) === urlGid);
                if (match) {
                    setSelectedSlotId(String(match.slotId));
                    setGroupId(String(match.groupId));
                }
            }
        } catch {
            setSessions([]);
            alert(t('attendanceCalendar:loadSessionsError'));
        }
    }

    function selectSession(sess) {
        setSelectedSlotId(String(sess.slotId));
        setGroupId(String(sess.groupId));
    }

    async function loadDayAttendance() {
        if (!groupId || !schoolId) return;
        try {
            const res = await api.get('/attendance', { params: { groupId, date } });
            const payload = res.data;
            const list = Array.isArray(payload) ? payload : (payload.students || []);
            const hasSession = Array.isArray(payload) ? true : (payload.hasSession !== false);
            const defaultAssume = Array.isArray(payload) ? true : !!payload.defaultAssumeAttending;
            setSessionInfo({ hasSession, defaultAssumeAttending: defaultAssume });
            setStudents(list);
            const m = {};
            list.forEach(s => {
                m[s.studentId] = { isPresent: s.isPresent ?? defaultAssume, note: s.note || '' };
            });
            setMarks(m);
        } catch (err) {
            const status = err.response?.status;
            const msg = err.response?.data?.message;
            if (status === 403) {
                alert(t('attendanceCalendar:noAccess'));
            } else {
                alert(msg || t('attendanceCalendar:loadAttendanceError'));
            }
            setStudents([]);
            setMarks({});
        }
    }

    async function loadMonthCalendar() {
        const d = new Date(date);
        try {
            const res = await api.get('/attendance/calendar', {
                params: { groupId, year: d.getFullYear(), month: d.getMonth() + 1 }
            });
            setCalendar(res.data);
        } catch { alert(t('attendanceCalendar:loadCalendarError')); }
    }

    function togglePresence(studentId) {
        setMarks(prev => ({
            ...prev,
            [studentId]: { ...prev[studentId], isPresent: !prev[studentId]?.isPresent }
        }));
    }

    function markAll(isPresent) {
        const m = {};
        students.forEach(s => {
            m[s.studentId] = { isPresent, note: marks[s.studentId]?.note || '' };
        });
        setMarks(m);
    }

    async function handleSave() {
        if (saving) return;
        if (!sessionInfo.hasSession || students.length === 0) {
            alert(t('attendanceCalendar:noSession'));
            return;
        }
        setSaving(true);
        try {
            await api.post('/attendance/mark', {
                groupId: parseInt(groupId),
                date,
                students: students.map(s => ({
                    studentId: s.studentId,
                    isPresent: marks[s.studentId]?.isPresent ?? true,
                    note: marks[s.studentId]?.note || null
                }))
            });
            alert(t('attendanceCalendar:saved'));
        } catch { alert(t('attendanceCalendar:saveError')); }
        setSaving(false);
    }

    const presentCount = students.filter(s => marks[s.studentId]?.isPresent).length;
    const absentCount = students.length - presentCount;

    return (
        <OwnerPageLayout
            title={t('attendanceCalendar:title')}
            subtitle={coachMode
                ? t('attendanceCalendar:subtitleCoach')
                : t('attendanceCalendar:subtitleOwner')}
            maxWidth={960}
        >
                <div className="sp-panel sp-toolbar" style={{ marginBottom: 20 }}>
                    <input
                        type="date" value={date}
                        onChange={e => setDate(e.target.value)}
                        style={{
                            padding: '10px 14px', borderRadius: '10px',
                            border: '1.5px solid var(--sp-gray-200)', fontSize: '14px', outline: 'none'
                        }}
                    />

                    {!coachMode && (
                        <select
                            value={groupId}
                            onChange={e => { setGroupId(e.target.value); setSelectedSlotId(''); }}
                            style={{
                                padding: '10px 14px', borderRadius: '10px',
                                border: '1.5px solid var(--sp-gray-200)', fontSize: '14px',
                                outline: 'none', background: 'white', minWidth: '180px'
                            }}
                        >
                            <option value="">{t('attendanceCalendar:pickGroup')}</option>
                            {groups.map(g => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                        </select>
                    )}

                    {!coachMode && (
                        <div style={{
                            display: 'flex', borderRadius: '10px', overflow: 'hidden',
                            border: '1.5px solid var(--sp-gray-200)', marginLeft: 'auto'
                        }}>
                            {['day', 'month'].map(v => (
                                <button key={v} onClick={() => setView(v)} style={{
                                    padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '14px',
                                    background: view === v ? '#1A1A1A' : 'white',
                                    color: view === v ? 'white' : '#666',
                                    fontWeight: view === v ? '700' : '400'
                                }}>
                                    {v === 'day' ? t('attendanceCalendar:viewDay') : t('attendanceCalendar:viewMonth')}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {coachMode && (
                    <div style={{
                        background: 'white', borderRadius: '14px', padding: '16px 20px',
                        marginBottom: '20px', border: '1px solid var(--sp-gray-100)'
                    }}>
                        <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: 700 }}>{t('attendanceCalendar:sessionsTitle')}</h3>
                        {sessions.length === 0 ? (
                            <p style={{ color: '#888', margin: 0, fontSize: '14px' }}>
                                {t('attendanceCalendar:noSessions')}
                            </p>
                        ) : (
                            <div style={{ display: 'grid', gap: '8px' }}>
                                {sessions.map(sess => {
                                    const active = selectedSlotId === String(sess.slotId);
                                    return (
                                        <button
                                            key={sess.slotId}
                                            type="button"
                                            onClick={() => selectSession(sess)}
                                            style={{
                                                textAlign: 'left', padding: '12px 16px',
                                                borderRadius: '10px', cursor: 'pointer',
                                                border: active ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                                                background: active ? '#eef2ff' : '#fff'
                                            }}
                                        >
                                            <div style={{ fontWeight: 700, color: '#1A1A1A' }}>
                                                {sess.startTime}–{sess.endTime} · {sess.groupName}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                                                {sess.roomName || t('attendanceCalendar:noRoom')}
                                                {' · '}{t('attendanceCalendar:expectedStudents', { count: sess.expectedStudents })}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* ВИД: ДЕНЬ */}
                {view === 'day' && (
                    <div style={{
                        background: 'white', borderRadius: '16px',
                        padding: '24px', border: '1px solid var(--sp-gray-100)'
                    }}>
                        <div style={{
                            display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px'
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#1A1A1A' }}>
                                    {formatDate(`${date}T12:00:00`, { weekday: 'long', month: 'long', day: 'numeric' })}
                                    {coachMode && selectedSlotId && (() => {
                                        const s = sessions.find(x => String(x.slotId) === selectedSlotId);
                                        return s ? (
                                            <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#4f46e5', marginTop: '4px' }}>
                                                {s.startTime}–{s.endTime} · {s.groupName}
                                            </span>
                                        ) : null;
                                    })()}
                                </h3>
                                {students.length > 0 && (
                                    <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
                                        <span style={{ fontSize: '13px', color: '#2e7d32', fontWeight: '600' }}>
                                            {t('attendanceCalendar:presentCount', { count: presentCount })}
                                        </span>
                                        <span style={{ fontSize: '13px', color: '#c62828', fontWeight: '600' }}>
                                            {t('attendanceCalendar:absentCount', { count: absentCount })}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => markAll(true)} style={{
                                    padding: '8px 16px', borderRadius: '8px',
                                    background: '#e8f5e9', color: '#2e7d32',
                                    border: '1px solid #c8e6c9', cursor: 'pointer', fontSize: '13px', fontWeight: '600'
                                }}>
                                    {t('attendanceCalendar:markAllPresent')}
                                </button>
                                <button onClick={() => markAll(false)} style={{
                                    padding: '8px 16px', borderRadius: '8px',
                                    background: '#fce4ec', color: '#c62828',
                                    border: '1px solid #ffcdd2', cursor: 'pointer', fontSize: '13px', fontWeight: '600'
                                }}>
                                    {t('attendanceCalendar:markAllAbsent')}
                                </button>
                            </div>
                        </div>

                        {groupId && !sessionInfo.hasSession && (
                            <div style={{
                                padding: '16px', marginBottom: '16px', borderRadius: '10px',
                                background: '#fff8e1', color: '#92400e', fontSize: '14px'
                            }}>
                                {t('attendanceCalendar:noSessionForGroup')}
                            </div>
                        )}
                        {groupId && sessionInfo.hasSession && (
                            <div style={{
                                padding: '10px 14px', marginBottom: '16px', borderRadius: '10px',
                                background: '#f0f9ff', color: '#0369a1', fontSize: '13px'
                            }}>
                                {sessionInfo.defaultAssumeAttending
                                    ? t('attendanceCalendar:hintDefaultAll')
                                    : t('attendanceCalendar:hintPlannedOnly')}
                            </div>
                        )}

                        {!groupId ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>
                                <div style={{ fontSize: '36px', marginBottom: '10px' }}>👆</div>
                                {coachMode ? t('attendanceCalendar:pickSession') : t('attendanceCalendar:pickGroupAbove')}
                            </div>
                        ) : students.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>
                                <div style={{ fontSize: '36px', marginBottom: '10px' }}>👥</div>
                                {t('attendanceCalendar:noStudentsInGroup')}
                            </div>
                        ) : (
                            <>
                                <div style={{ display: 'grid', gap: '8px', marginBottom: '20px' }}>
                                    {students.map(s => {
                                        const isPresent = marks[s.studentId]?.isPresent ?? true;
                                        return (
                                            <div key={s.studentId} style={{
                                                display: 'flex', alignItems: 'center', gap: '12px',
                                                padding: '12px 16px', borderRadius: '10px',
                                                background: isPresent ? '#f0fff4' : '#fff5f5',
                                                border: `1px solid ${isPresent ? '#c8e6c9' : '#ffcdd2'}`,
                                                transition: 'all 0.15s'
                                            }}>
                                                <button
                                                    onClick={() => togglePresence(s.studentId)}
                                                    style={{
                                                        width: '40px', height: '40px', borderRadius: '50%',
                                                        border: 'none', cursor: 'pointer', fontSize: '18px',
                                                        background: isPresent ? '#2ecc71' : '#e74c3c',
                                                        color: 'white', flexShrink: 0,
                                                        boxShadow: `0 2px 8px ${isPresent ? 'rgba(46,204,113,0.4)' : 'rgba(231,76,60,0.4)'}`
                                                    }}
                                                >
                                                    {isPresent ? '✓' : '✗'}
                                                </button>
                                                <span style={{ flex: 1, fontSize: '15px', fontWeight: '600', color: '#1A1A1A' }}>
                                                    {s.fullName}
                                                </span>
                                                <input
                                                    value={marks[s.studentId]?.note || ''}
                                                    onChange={e => setMarks(prev => ({
                                                        ...prev,
                                                        [s.studentId]: { ...prev[s.studentId], note: e.target.value }
                                                    }))}
                                                    placeholder={t('attendanceCalendar:notePlaceholder')}
                                                    style={{
                                                        padding: '7px 12px', border: '1.5px solid var(--sp-gray-200)',
                                                        borderRadius: '8px', fontSize: '13px', width: '200px',
                                                        outline: 'none', background: 'white'
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={handleSave} disabled={saving}
                                    style={{
                                        padding: '13px 36px', background: saving ? '#94a3b8' : '#1A1A1A',
                                        color: 'white', border: 'none', borderRadius: '10px',
                                        fontSize: '15px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {saving ? t('attendanceCalendar:saving') : t('attendanceCalendar:saveAttendance')}
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* ВИД: МЕСЯЦ */}
                {view === 'month' && (
                    <div style={{
                        background: 'white', borderRadius: '16px',
                        padding: '24px', border: '1px solid var(--sp-gray-100)'
                    }}>
                        <h3 style={{ margin: '0 0 20px', fontSize: '17px', fontWeight: '700', color: '#1A1A1A' }}>
                            {months[new Date(date).getMonth()]} {new Date(date).getFullYear()}
                        </h3>

                        {calendar.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>
                                <div style={{ fontSize: '36px', marginBottom: '10px' }}>📊</div>
                                {t('attendanceCalendar:noMonthData')}
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ background: 'var(--sp-gray-50)' }}>
                                            {monthHeaders.map(h => (
                                                <th key={h} style={{
                                                    padding: '12px 16px', textAlign: 'left',
                                                    fontWeight: '700', color: '#555', fontSize: '13px'
                                                }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {calendar.map(s => {
                                            const pct = s.totalDays > 0
                                                ? Math.round((s.presentDays / s.totalDays) * 100) : 0;
                                            return (
                                                <tr key={s.studentId} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '12px 16px', fontWeight: '600' }}>{s.fullName}</td>
                                                    <td style={{ padding: '12px 16px', textAlign: 'center', color: '#666' }}>{s.totalDays}</td>
                                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                        <span style={{ color: '#2e7d32', fontWeight: '600' }}>{s.presentDays}</span>
                                                    </td>
                                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                        <span style={{ color: '#c62828', fontWeight: '600' }}>{s.absentDays}</span>
                                                    </td>
                                                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                        <span style={{
                                                            background: pct >= 75 ? '#e8f5e9' : pct >= 50 ? '#fff3e0' : '#fce4ec',
                                                            color: pct >= 75 ? '#2e7d32' : pct >= 50 ? '#e65100' : '#c62828',
                                                            padding: '4px 12px', borderRadius: '12px',
                                                            fontSize: '13px', fontWeight: '700'
                                                        }}>
                                                            {pct}%
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
        </OwnerPageLayout>
    );
}